import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';
import { ModalBase } from '../../supervisor/components/modals/ModalBase';

import { PROPOSALS_QUERY_KEY } from './use-my-proposals';

import type { ConductionProposalWire } from '../../../lib/api-client';
import type React from 'react';

export interface ProposalReviewModalProps {
  proposal: ConductionProposalWire | null;
  onClose: () => void;
}

/**
 * ProposalReviewModal — Item 368 F2 Fatia D.
 *
 * Mostra o que a IA PROPÕE fazer (summary vindo do backend, nunca fabricado)
 * e oferece duas ações ao atendente: Conceder (H_c) ou Recusar (mantém H_a).
 * Recusar permite motivo opcional (campo de texto livre).
 *
 * Reusa ModalBase canônico do projeto (supervisor/components/modals/ModalBase.tsx).
 */
export function ProposalReviewModal({
  proposal,
  onClose,
}: ProposalReviewModalProps): React.JSX.Element | null {
  const push = useToastStore((s) => s.push);
  const queryClient = useQueryClient();

  const [isPending, setIsPending] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineInput, setShowDeclineInput] = useState(false);
  const [failed, setFailed] = useState(false);

  if (proposal === null) return null;

  // Narrowed — safe after early return above.
  const p = proposal;
  const confidencePct = Math.round(p.confidence * 100);

  function handleClose(): void {
    setDeclineReason('');
    setShowDeclineInput(false);
    setFailed(false);
    onClose();
  }

  async function handleGrant(): Promise<void> {
    setIsPending(true);
    setFailed(false);
    try {
      await api.grantConductionProposal(p.proposalId);
      void queryClient.invalidateQueries({ queryKey: PROPOSALS_QUERY_KEY });
      push({ variant: 'success', title: 'IA autorizada a conduzir esta conversa.' });
      handleClose();
    } catch {
      setFailed(true);
    } finally {
      setIsPending(false);
    }
  }

  async function handleDeny(): Promise<void> {
    setIsPending(true);
    setFailed(false);
    try {
      const reason = declineReason.trim() !== '' ? declineReason.trim() : undefined;
      await api.denyConductionProposal(p.proposalId, reason);
      void queryClient.invalidateQueries({ queryKey: PROPOSALS_QUERY_KEY });
      push({ variant: 'success', title: 'Pedido da IA recusado. Conversa continua com você.' });
      handleClose();
    } catch {
      setFailed(true);
    } finally {
      setIsPending(false);
    }
  }

  return (
    <ModalBase
      open
      onClose={handleClose}
      title="Pedido da IA"
      width={520}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button
            className="btn-ghost-mini"
            type="button"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancelar
          </button>
          <button
            className="btn-ghost-mini"
            type="button"
            disabled={isPending}
            onClick={() => {
              if (showDeclineInput) {
                void handleDeny();
              } else {
                setShowDeclineInput(true);
              }
            }}
          >
            {isPending && showDeclineInput ? 'Recusando…' : 'Recusar'}
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={isPending || showDeclineInput}
            onClick={() => void handleGrant()}
          >
            {isPending && !showDeclineInput ? 'Concedendo…' : 'Conceder'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        {p.personDisplayName !== null && (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-2)', marginBottom: 4 }}>
            Conversa com <b>{p.personDisplayName}</b>
          </p>
        )}

        <div
          style={{
            background: 'var(--bg-2)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3)',
            marginBottom: 'var(--space-3)',
          }}
        >
          <p
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--fg-1)',
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {p.summary}
          </p>
        </div>

        <p
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--fg-3)',
            marginBottom: 'var(--space-3)',
          }}
        >
          Confiança da IA: {confidencePct}%
        </p>

        {showDeclineInput && (
          <label className="modal-form__field">
            <span className="modal-form__label">Motivo da recusa (opcional)</span>
            <textarea
              className="modal-form__textarea"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={2}
              placeholder="Ex.: cliente em estado emocional delicado…"
              autoFocus
            />
          </label>
        )}

        {failed && (
          <div className="banner banner--danger" role="alert">
            <div className="banner__body">
              <span className="banner__title">
                Não foi possível processar o pedido. Tente novamente.
              </span>
            </div>
          </div>
        )}
      </div>
    </ModalBase>
  );
}
